import React, { useState, useRef, useEffect } from "react";
import { useReactToPrint } from "react-to-print";
import { QRCodeSVG } from "qrcode.react";
import { getCurrentUser } from "aws-amplify/auth";
import { Plus, Search, Printer, Eye, CheckCircle, XCircle, IndianRupee, X, Bluetooth } from "lucide-react";
import { formatCurrency, formatDate, COMMITTEE } from "../utils/mockData";
import { donationsApi, whatsappApi } from "../utils/api";
import { btPrintReceipt, isBluetoothSupported } from "../utils/btPrint";

const paymentModes = ["Cash", "UPI", "NEFT", "Cheque", "Card"];

async function transliterateToTelugu(text) {
  const query = new URLSearchParams({ text, itc: "te-t-i0-und", num: "1" });
  const res = await fetch(`https://inputtools.google.com/request?${query}`);
  const data = await res.json();
  if (data[0] !== "SUCCESS") throw new Error("Transliteration failed");
  return data[1][0][1][0];
}

const RS = {
  receipt: {
    width: "54mm", fontFamily: "'Courier New', Courier, monospace", fontSize: "8pt",
    lineHeight: 1.3, color: "#000", backgroundColor: "#fff", padding: "2mm", margin: "0 auto",
    boxSizing: "border-box", wordBreak: "break-word",
  },
  center: { textAlign: "center" },
  dashedBottom: { borderBottom: "1px dashed #000", paddingBottom: "1.5mm", marginBottom: "1.5mm" },
  dashedTop: { borderTop: "1px dashed #000", paddingTop: "1.5mm", marginTop: "1.5mm" },
  row: { display: "flex", justifyContent: "space-between", fontSize: "7pt" },
  label: { fontSize: "6pt", textTransform: "uppercase", letterSpacing: "0.5px" },
  amountBox: { textAlign: "center", borderTop: "1px dashed #000", borderBottom: "1px dashed #000", padding: "2mm 0", margin: "2mm 0" },
  amountNum: { fontSize: "16pt", fontWeight: "bold", letterSpacing: "1px" },
  footer: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginTop: "2mm" },
  sigLine: { borderTop: "1px solid #000", paddingTop: "0.5mm", marginTop: "7mm", fontWeight: "bold", fontSize: "7pt" },
  smallText: { fontSize: "6pt" },
  footNote: { textAlign: "center", fontSize: "6pt", borderTop: "1px dashed #000", marginTop: "2mm", paddingTop: "1.5mm" },
};

const ReceiptPrint = React.forwardRef(function ReceiptPrint({ donation }, ref) {
  if (!donation) return null;
  return (
    <div ref={ref} style={RS.receipt}>
      <div style={{ ...RS.center, ...RS.dashedBottom }}>
        <img src="/logo.png" alt="Logo" style={{ height: "12mm", width: "12mm", display: "block", margin: "0 auto 1mm" }} onError={(e) => { e.target.style.display = "none"; }} />
        <div style={{ fontSize: "9pt", fontWeight: "bold", lineHeight: 1.2 }}>{COMMITTEE.name}</div>
        <div style={RS.smallText}>{COMMITTEE.address}</div>
        <div style={RS.smallText}>Ph: {COMMITTEE.phone}</div>
      </div>

      <div style={{ ...RS.center, fontWeight: "bold", fontSize: "8pt", border: "1px solid #000", padding: "1mm", marginBottom: "2mm", letterSpacing: "2px" }}>
        DONATION RECEIPT
      </div>

      <div style={RS.row}>
        <span><span style={RS.label}>Receipt# </span>{donation.receiptNo}</span>
        <span>{formatDate(donation.date)}{donation.time ? ` ${donation.time}` : ""}</span>
      </div>

      <div style={RS.dashedTop}>
        <div><span style={RS.label}>Name: </span><strong>{donation.donorName}</strong></div>
        <div><span style={RS.label}>Phone: </span>{donation.phone}</div>
        {donation.address && <div><span style={RS.label}>Addr: </span>{donation.address}</div>}
        <div><span style={RS.label}>Mode: </span>{donation.paymentMode}</div>
      </div>

      <div style={RS.amountBox}>
        <div style={{ ...RS.label, marginBottom: "1mm" }}>Amount Received</div>
        <div style={RS.amountNum}>{formatCurrency(Number(donation.amount || 0))}</div>
        <div style={{ ...RS.smallText, marginTop: "1mm", fontStyle: "italic" }}>
          {amountInWords(Number(donation.amount || 0))} Rupees Only
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: "2mm" }}>
        <QRCodeSVG value={COMMITTEE.communitylink} size={70} level="M" />
        <div style={{ ...RS.smallText, marginTop: "0.5mm" }}>Join WhatsApp Channel</div>
      </div>

      {donation.receivedBy && (
        <div style={{ ...RS.smallText, marginTop: "2mm" }}>Added by: {donation.receivedBy}</div>
      )}

      <div style={RS.footNote}>
        <div>Ganesh Chaturthi 2026 — utsav.btrix.in</div>
        <div>Computer Generated Receipt</div>
      </div>
    </div>
  );
});

function amountInWords(amount) {
  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

  if (amount === 0) return "Zero";
  if (amount < 20) return ones[amount];
  if (amount < 100) return tens[Math.floor(amount / 10)] + (amount % 10 ? " " + ones[amount % 10] : "");
  if (amount < 1000) return ones[Math.floor(amount / 100)] + " Hundred" + (amount % 100 ? " " + amountInWords(amount % 100) : "");
  if (amount < 100000) return amountInWords(Math.floor(amount / 1000)) + " Thousand" + (amount % 1000 ? " " + amountInWords(amount % 1000) : "");
  if (amount < 10000000) return amountInWords(Math.floor(amount / 100000)) + " Lakh" + (amount % 100000 ? " " + amountInWords(amount % 100000) : "");
  return amountInWords(Math.floor(amount / 10000000)) + " Crore" + (amount % 10000000 ? " " + amountInWords(amount % 10000000) : "");
}

function DonationModal({ onClose, onSave, saving }) {
  const [form, setForm] = useState({
    donorName: "", teluguName: "", soName: "", phone: "", address: "", amount: "", paymentMode: "Cash",
    date: new Date().toISOString().split("T")[0],
    time: new Date().toTimeString().slice(0, 5),
  });
  const [errors, setErrors] = useState({});
  const [teluguTouched, setTeluguTouched] = useState(false);
  const [teluguLoading, setTeluguLoading] = useState(false);

  useEffect(() => {
    if (teluguTouched || !form.donorName.trim()) return;
    let cancelled = false;
    setTeluguLoading(true);
    const timer = setTimeout(() => {
      transliterateToTelugu(form.donorName.trim())
        .then((telugu) => {
          if (!cancelled) setForm((f) => ({ ...f, teluguName: telugu }));
        })
        .catch(() => {})
        .finally(() => !cancelled && setTeluguLoading(false));
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); setTeluguLoading(false); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.donorName, teluguTouched]);

  const validate = () => {
    const e = {};
    if (!form.donorName.trim()) e.donorName = "Name required";
    if (!form.phone.match(/^\d{10}$/)) e.phone = "Valid 10-digit phone required";
    if (!form.amount || isNaN(form.amount) || Number(form.amount) <= 0) e.amount = "Valid amount required";

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    onSave({ ...form, status: "completed", amount: Number(form.amount), time: form.time });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl shadow-2xl z-10 max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between rounded-t-2xl">
          <div>
            <h2 className="text-base font-bold text-gray-900">Record Donation</h2>
            <p className="text-xs text-gray-400">Ganesh Chaturthi 2026</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Name */}
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Donor Name *</label>
            <input
              type="text"
              value={form.donorName}
              onChange={(e) => setForm({ ...form, donorName: e.target.value })}
              placeholder="Enter full name"
              className={`w-full border ${errors.donorName ? "border-red-400" : "border-gray-200"} rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent`}
            />
            {errors.donorName && <p className="text-xs text-red-500 mt-0.5">{errors.donorName}</p>}
          </div>

          {/* Telugu name override */}
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Name in Telugu</label>
            <div className="relative">
              <input
                type="text"
                value={form.teluguName}
                onChange={(e) => { setTeluguTouched(true); setForm({ ...form, teluguName: e.target.value }); }}
                placeholder="Auto-fills as you type the name above"
                className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
              />
              {teluguLoading && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 border-2 border-orange-200 border-t-orange-500 rounded-full animate-spin" />
              )}
            </div>
            <p className="text-xs text-gray-400 mt-0.5">
              {teluguTouched ? (
                <>
                  Manually edited — used as-is in the Telugu WhatsApp receipt.{" "}
                  <button
                    type="button"
                    onClick={() => setTeluguTouched(false)}
                    className="text-orange-500 font-semibold underline"
                  >
                    Reset to auto-translation
                  </button>
                </>
              ) : (
                "Check this matches the name correctly, then edit if it doesn't."
              )}
            </p>
          </div>

          {/* S/O */}
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">S/D/O (Son/Daughter/Wife of)</label>
            <input
              type="text"
              value={form.soName}
              onChange={(e) => setForm({ ...form, soName: e.target.value })}
              placeholder="Optional"
              className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
          </div>

          {/* Phone */}
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Phone *</label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="10-digit number"
              maxLength={10}
              className={`w-full border ${errors.phone ? "border-red-400" : "border-gray-200"} rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400`}
            />
            {errors.phone && <p className="text-xs text-red-500 mt-0.5">{errors.phone}</p>}
          </div>

          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Date</label>
              <input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Time</label>
              <input
                type="time"
                value={form.time}
                onChange={(e) => setForm({ ...form, time: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
              />
            </div>
          </div>

          {/* Address */}
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Address</label>
            <input
              type="text"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              placeholder="Colony, City"
              className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
          </div>

          {/* Amount */}
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Amount (₹) *</label>
            <div className="relative">
              <IndianRupee size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="number"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder="0"
                min="1"
                className={`w-full border ${errors.amount ? "border-red-400" : "border-gray-200"} rounded-xl pl-8 pr-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400`}
              />
            </div>
            {errors.amount && <p className="text-xs text-red-500 mt-0.5">{errors.amount}</p>}
            {form.amount && !errors.amount && (
              <p className="text-xs text-orange-600 mt-0.5 font-medium">
                {amountInWords(Number(form.amount))} Rupees
              </p>
            )}
          </div>

          {/* Payment Mode */}
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Payment Mode</label>
            <select
              value={form.paymentMode}
              onChange={(e) => setForm({ ...form, paymentMode: e.target.value })}
              className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            >
              {paymentModes.map((m) => <option key={m}>{m}</option>)}
            </select>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={saving}
            className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bold py-3 rounded-xl transition-colors shadow-lg shadow-orange-500/20 flex items-center justify-center gap-2"
          >
            {saving && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
            {saving ? "Saving..." : "Save & Generate Receipt"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function Donations() {
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [filterMode, setFilterMode] = useState("All");
  const [showModal, setShowModal] = useState(false);
  const [selectedDonation, setSelectedDonation] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [currentUser, setCurrentUser] = useState("Admin");
  const [btStatus, setBtStatus] = useState("idle");
  const [btError, setBtError] = useState("");
  const printRef = useRef();

  const handleBtPrint = async () => {
    setBtStatus("printing");
    setBtError("");
    try {
      await btPrintReceipt(selectedDonation, COMMITTEE);
      setBtStatus("done");
      setTimeout(() => setBtStatus("idle"), 3000);
    } catch (e) {
      setBtError(e.message || "Bluetooth print failed");
      setBtStatus("error");
      setTimeout(() => setBtStatus("idle"), 5000);
    }
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    pageStyle: `@page { size: 58mm auto; margin: 0; } @media print { html, body { width: 58mm; margin: 0; padding: 0; } }`,
  });

  useEffect(() => {
    donationsApi.list()
      .then((res) => setDonations(res.donations || []))
      .catch(console.error)
      .finally(() => setLoading(false));

    getCurrentUser()
      .then((user) => setCurrentUser(user.username || "Admin"))
      .catch(() => setCurrentUser("Admin"));
  }, []);

  const filtered = donations.filter((d) => {
    const matchSearch = d.donorName.toLowerCase().includes(search.toLowerCase()) ||
      d.phone.includes(search) || d.receiptNo?.toLowerCase().includes(search.toLowerCase());
    const matchMode = filterMode === "All" || d.paymentMode === filterMode;
    return matchSearch && matchMode;
  });

  const handleSave = async (donation) => {
    setSaving(true);
    try {
      // Save to DynamoDB via Lambda
      const res = await donationsApi.create({ ...donation, committeeId: "ssvus", receivedBy: currentUser });
      const saved = res.donation || donation;
      setDonations((prev) => [saved, ...prev]);
      setShowModal(false);
      setSelectedDonation(saved);
      setShowReceipt(true);

      // Auto-send WhatsApp receipt if phone is available
      if (saved.phone) {
        whatsappApi.send({
          type: "receipt",
          recipients: [{ name: saved.donorName, phone: saved.phone }],
          donation: saved,
          committeeName: COMMITTEE.name,
        }).catch(() => {}); // don't block UI if WhatsApp fails
      }
    } catch {
      // API failed — save locally so work isn't lost
      const localDonation = { ...donation, receivedBy: currentUser };
      setDonations((prev) => [localDonation, ...prev]);
      setShowModal(false);
      setSelectedDonation(localDonation);
      setShowReceipt(true);
    } finally {
      setSaving(false);
    }
  };

  const handleViewReceipt = (donation) => {
    setSelectedDonation(donation);
    setShowReceipt(true);
  };

  const totalFiltered = filtered.reduce((s, d) => s + Number(d.amount || 0), 0);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-orange-200 border-t-orange-500 rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="p-4 lg:p-6 space-y-4 fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Donations</h1>
          <p className="text-sm text-gray-500">{donations.length} donors • {formatCurrency(donations.reduce((s, d) => s + Number(d.amount || 0), 0))} collected</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-4 py-2.5 rounded-xl transition-colors shadow-lg shadow-orange-500/20 text-sm"
        >
          <Plus size={16} /> Record Donation
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, phone, receipt..."
            className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {["All", ...paymentModes].map((m) => (
            <button
              key={m}
              onClick={() => setFilterMode(m)}
              className={`px-3 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-colors ${
                filterMode === m
                  ? "bg-orange-500 text-white shadow-sm"
                  : "bg-white border border-gray-200 text-gray-600 hover:border-orange-300"
              }`}
            >
              {m}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Bar */}
      <div className="bg-orange-50 border border-orange-100 rounded-xl px-4 py-2.5 flex items-center justify-between">
        <span className="text-xs text-orange-700 font-medium">{filtered.length} results</span>
        <span className="text-sm font-bold text-orange-700">{formatCurrency(totalFiltered)}</span>
      </div>

      {/* Table / Cards */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Receipt</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Donor</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Date & Time</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Mode</th>
                <th className="text-right text-xs font-semibold text-gray-500 px-4 py-3">Amount</th>
                <th className="text-center text-xs font-semibold text-gray-500 px-4 py-3">Status</th>
                <th className="text-center text-xs font-semibold text-gray-500 px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map((d) => (
                <tr key={d.id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-4 py-3">
                    <span className="text-xs font-mono text-orange-600 font-semibold">{d.receiptNo}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 bg-orange-100 rounded-full flex items-center justify-center text-xs font-bold text-orange-600">
                        {d.donorName.charAt(0)}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-800">{d.donorName}</p>
                        <p className="text-xs text-gray-400">{d.phone}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">
                    <div>{formatDate(d.date)}</div>
                    {d.time && <div className="text-gray-400">{d.time}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      d.paymentMode === "Cash" ? "bg-green-50 text-green-700" :
                      d.paymentMode === "UPI" ? "bg-blue-50 text-blue-700" :
                      d.paymentMode === "NEFT" ? "bg-purple-50 text-purple-700" :
                      "bg-gray-100 text-gray-600"
                    }`}>
                      {d.paymentMode}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-sm font-bold text-gray-900">{formatCurrency(d.amount)}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {d.status === "completed"
                      ? <CheckCircle size={15} className="text-green-500 mx-auto" />
                      : <XCircle size={15} className="text-yellow-500 mx-auto" />}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1.5">
                      <button
                        onClick={() => handleViewReceipt(d)}
                        className="p-1.5 hover:bg-orange-50 rounded-lg text-gray-400 hover:text-orange-500 transition-colors"
                        title="View Receipt"
                      >
                        <Eye size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden divide-y divide-gray-100">
          {filtered.map((d) => (
            <div key={d.id} className="p-4 hover:bg-gray-50/50">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 bg-orange-100 rounded-full flex items-center justify-center text-sm font-bold text-orange-600">
                    {d.donorName.charAt(0)}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{d.donorName}</p>
                    <p className="text-xs text-gray-400">{d.phone}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-base font-black text-green-600">{formatCurrency(d.amount)}</p>
                  <span className="text-[10px] font-mono text-orange-500">{d.receiptNo}</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex gap-2">
                  <span className="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">{d.paymentMode}</span>
                  <span className="text-[10px] bg-orange-50 text-orange-600 px-2 py-0.5 rounded-full font-medium">{d.purpose}</span>
                </div>
                <button
                  onClick={() => handleViewReceipt(d)}
                  className="flex items-center gap-1 text-xs text-orange-500 font-semibold"
                >
                  <Printer size={12} /> Print
                </button>
              </div>
            </div>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-400 text-sm">No donations found</p>
          </div>
        )}
      </div>

      {/* Modals */}
      {showModal && <DonationModal onClose={() => setShowModal(false)} onSave={handleSave} saving={saving} />}

      {/* Receipt Preview Modal */}
      {showReceipt && selectedDonation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowReceipt(false)} />
          <div className="relative bg-gray-100 rounded-2xl shadow-2xl z-10 w-full max-w-xs max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-4 py-3 rounded-t-2xl z-10">
              <div className="flex items-center justify-between mb-1">
                <h2 className="text-xs font-bold text-gray-700 font-mono">{selectedDonation.receiptNo}</h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handlePrint}
                    className="flex items-center gap-1.5 bg-orange-500 text-white text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-orange-600"
                  >
                    <Printer size={12} /> Print
                  </button>
                  {isBluetoothSupported() && (
                    <button
                      onClick={handleBtPrint}
                      disabled={btStatus === "printing"}
                      className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${
                        btStatus === "done" ? "bg-green-500 text-white" :
                        btStatus === "error" ? "bg-red-500 text-white" :
                        btStatus === "printing" ? "bg-blue-400 text-white opacity-70" :
                        "bg-blue-600 text-white hover:bg-blue-700"
                      }`}
                    >
                      <Bluetooth size={12} />
                      {btStatus === "printing" ? "Printing…" : btStatus === "done" ? "Sent!" : btStatus === "error" ? "Failed" : "BT Print"}
                    </button>
                  )}
                  <button onClick={() => { setShowReceipt(false); setBtStatus("idle"); }} className="p-1.5 hover:bg-gray-100 rounded-lg">
                    <X size={16} className="text-gray-500" />
                  </button>
                </div>
              </div>
              {btStatus === "error" && btError && (
                <p className="text-[10px] text-red-500 mt-0.5">{btError}</p>
              )}
            </div>
            <div className="flex justify-center py-4 bg-gray-100">
              <div className="bg-white shadow-md">
                <ReceiptPrint ref={printRef} donation={selectedDonation} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
